/**
 * React Hook for Chat End-to-End Encryption (Web)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  generateKeyPair,
  deriveSharedKey,
  createEncryptedMessage,
  decryptEncryptedMessage,
  encryptFile,
  decryptFile,
  isEncryptionAvailable,
} from './chatEncryption';

const ENCRYPTION_KEYS_PREFIX = 'cmv_chat_encryption_';

export const useChatEncryption = (chatId) => {
  const [state, setState] = useState({
    isInitialized: false,
    isEnabled: false,
    hasSharedKey: false,
    error: null,
  });

  const [publicKey, setPublicKey] = useState(null);
  const keysRef = useRef(null);
  const sharedKeyRef = useRef(null);
  const isAvailable = isEncryptionAvailable();

  const loadOrGenerateKeys = useCallback(async () => {
    // Stable per-DEVICE keypair (NOT per-chat) so a sender can encrypt the very
    // first message to us with our user-level-published key and we can decrypt it.
    // Legacy per-chat keys are still tried on decrypt for backward compatibility.
    const storageKey = `${ENCRYPTION_KEYS_PREFIX}device`;

    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const publicBytes = atob(parsed.publicKey || '').length;
        const privateBytes = atob(parsed.privateKey || '').length;
        if (publicBytes === 32 && privateBytes === 32) {
          const keys = { ...parsed, version: 5 };
          keysRef.current = keys;
          if (parsed.version !== 5) {
            try {
              window.localStorage.setItem(storageKey, JSON.stringify(keys));
            } catch (error) {
              console.warn('[Encryption] Failed to update key version:', error);
            }
          }
          return keys;
        }
      }
    } catch (error) {
      console.warn('[Encryption] Failed to load stored keys:', error);
    }

    const newKeys = await generateKeyPair();
    const versionedKeys = { ...newKeys, version: 5 };
    keysRef.current = versionedKeys;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(versionedKeys));
    } catch (error) {
      console.warn('[Encryption] Failed to store keys:', error);
    }

    return versionedKeys;
  }, []);

  // Legacy per-chat private key (from before the device-key migration). Tried as a
  // decrypt fallback so messages encrypted with old per-chat keys still open.
  const loadLegacyChatPrivateKey = useCallback(() => {
    if (!chatId) return null;
    try {
      const stored = window.localStorage.getItem(`${ENCRYPTION_KEYS_PREFIX}${chatId}`);
      if (stored) { const p = JSON.parse(stored); return p?.privateKey || null; }
    } catch { /* ignore */ }
    return null;
  }, [chatId]);

  const initializeEncryption = useCallback(async (chatIdParam, peerPublicKey) => {
    if (!isAvailable) {
      throw new Error('Encryption not available on this device');
    }

    try {
      setState(prev => ({ ...prev, error: null }));

      const keys = await loadOrGenerateKeys();
      setPublicKey(keys.publicKey);

      if (peerPublicKey) {
        const sharedKey = await deriveSharedKey(keys.privateKey, peerPublicKey);
        sharedKeyRef.current = sharedKey;
        setState({
          isInitialized: true,
          isEnabled: true,
          hasSharedKey: true,
          error: null,
        });
      } else {
        setState({
          isInitialized: true,
          isEnabled: true,
          hasSharedKey: false,
          error: null,
        });
      }

      return keys.publicKey;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setState(prev => ({ ...prev, error: message }));
      throw error;
    }
  }, [isAvailable, loadOrGenerateKeys]);

  const setPeerPublicKey = useCallback(async (peerPublicKey) => {
    if (!keysRef.current) {
      throw new Error('Encryption not initialized');
    }

    try {
      const sharedKey = await deriveSharedKey(keysRef.current.privateKey, peerPublicKey);
      sharedKeyRef.current = sharedKey;
      setState(prev => ({ ...prev, hasSharedKey: true, error: null }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setState(prev => ({ ...prev, error: message }));
      throw error;
    }
  }, []);

  const encryptMessage = useCallback(async (plaintext) => {
    if (!sharedKeyRef.current) {
      return null;
    }
    try {
      const encrypted = await createEncryptedMessage(plaintext, sharedKeyRef.current);
      return {
        ciphertext: encrypted.ciphertext,
        iv: encrypted.iv,
        version: encrypted.version,
      };
    } catch (error) {
      console.error('[Encryption] Failed to encrypt message:', error);
      return null;
    }
  }, []);

  const decryptMessage = useCallback(async (encrypted) => {
    if (!sharedKeyRef.current) {
      return '[Encrypted message - key not available]';
    }
    try {
      return await decryptEncryptedMessage({ encrypted: true, ...encrypted }, sharedKeyRef.current);
    } catch (error) {
      console.error('[Encryption] Failed to decrypt message:', error);
      return '[Failed to decrypt message]';
    }
  }, []);

  const encryptForPeer = useCallback(async (peerPublicKey, plaintext) => {
    if (!keysRef.current?.privateKey) {
      console.warn('[Encryption] encryptForPeer: No private key available, keysRef.current=', keysRef.current);
      return null;
    }
    try {
      const sharedKey = await deriveSharedKey(keysRef.current.privateKey, peerPublicKey);
      const encrypted = await createEncryptedMessage(plaintext, sharedKey);
      return {
        ciphertext: encrypted.ciphertext,
        iv: encrypted.iv,
        version: encrypted.version,
      };
    } catch (error) {
      console.error('[Encryption] Failed to encrypt for peer:', error);
      return null;
    }
  }, []);

  const decryptFromPeer = useCallback(async (peerPublicKey, encrypted) => {
    if (!keysRef.current?.privateKey) {
      return '[Encrypted message - key not available]';
    }
    try {
      const sharedKey = await deriveSharedKey(keysRef.current.privateKey, peerPublicKey);
      return await decryptEncryptedMessage({ encrypted: true, ...encrypted }, sharedKey);
    } catch (error) {
      return '[Encrypted message]';
    }
  }, []);

  /**
   * Decrypt a message trying multiple peer public keys (fallback for multi-device)
   */
  const decryptFromPeerWithFallback = useCallback(async (peerPublicKeys, encrypted) => {
    // Ensure keys are loaded (they may not be in keysRef yet)
    let privateKey = keysRef.current?.privateKey;
    if (!privateKey) {
      try {
        const keys = await loadOrGenerateKeys();
        privateKey = keys.privateKey;
      } catch {
        return { text: '[Encrypted message]', success: false };
      }
    }
    if (!privateKey) {
      return { text: '[Encrypted message - key not available]', success: false };
    }

    // Try our stable device key first, then the legacy per-chat key (old messages).
    const myPrivateKeys = [privateKey];
    const legacyPriv = loadLegacyChatPrivateKey();
    if (legacyPriv && legacyPriv !== privateKey) myPrivateKeys.push(legacyPriv);

    for (const myPriv of myPrivateKeys) {
      for (const peerKey of peerPublicKeys) {
        try {
          const sharedKey = await deriveSharedKey(myPriv, peerKey);
          const text = await decryptEncryptedMessage({ encrypted: true, ...encrypted }, sharedKey);
          return { text, success: true, usedKey: peerKey };
        } catch {
          // Try next combination
        }
      }
    }

    return { text: '[Encrypted message]', success: false };
  }, [loadOrGenerateKeys, loadLegacyChatPrivateKey]);

  const encryptFileData = useCallback(async (data) => {
    if (!sharedKeyRef.current) {
      return null;
    }
    try {
      return await encryptFile(data, sharedKeyRef.current);
    } catch (error) {
      console.error('[Encryption] Failed to encrypt file:', error);
      return null;
    }
  }, []);

  const encryptFileWithRawKey = useCallback(async (data, keyBytes) => {
    try {
      return await encryptFile(data, keyBytes);
    } catch (error) {
      console.error('[Encryption] Failed to encrypt file with raw key:', error);
      return null;
    }
  }, []);

  const decryptFileWithRawKey = useCallback(async (data, iv, keyBytes) => {
    try {
      return await decryptFile(data, iv, keyBytes);
    } catch (error) {
      return null;
    }
  }, []);

  const encryptFileForPeer = useCallback(async (peerPublicKey, data) => {
    if (!keysRef.current?.privateKey) {
      return null;
    }
    try {
      const sharedKey = await deriveSharedKey(keysRef.current.privateKey, peerPublicKey);
      return await encryptFile(data, sharedKey);
    } catch (error) {
      console.error('[Encryption] Failed to encrypt file for peer:', error);
      return null;
    }
  }, []);

  const decryptFileData = useCallback(async (data, iv) => {
    if (!sharedKeyRef.current) {
      return null;
    }
    return decryptFile(data, iv, sharedKeyRef.current);
  }, []);

  const decryptFileFromPeer = useCallback(async (peerPublicKey, data, iv) => {
    if (!keysRef.current?.privateKey) {
      return null;
    }
    try {
      const sharedKey = await deriveSharedKey(keysRef.current.privateKey, peerPublicKey);
      return await decryptFile(data, iv, sharedKey);
    } catch (error) {
      return null;
    }
  }, []);

  /**
   * Decrypt file data trying multiple peer public keys (fallback for multi-device)
   */
  const decryptFileFromPeerWithFallback = useCallback(async (peerPublicKeys, data, iv) => {
    // Ensure keys are loaded
    let privateKey = keysRef.current?.privateKey;
    if (!privateKey) {
      try {
        const keys = await loadOrGenerateKeys();
        privateKey = keys.privateKey;
      } catch {
        return null;
      }
    }
    if (!privateKey) {
      return null;
    }

    // Try our stable device key first, then the legacy per-chat key.
    const myPrivateKeys = [privateKey];
    const legacyPriv = loadLegacyChatPrivateKey();
    if (legacyPriv && legacyPriv !== privateKey) myPrivateKeys.push(legacyPriv);

    for (const myPriv of myPrivateKeys) {
      for (const peerKey of peerPublicKeys) {
        try {
          const sharedKey = await deriveSharedKey(myPriv, peerKey);
          const decryptedData = await decryptFile(data, iv, sharedKey);
          return { data: decryptedData, success: true, usedKey: peerKey };
        } catch {
          // Try next combination
        }
      }
    }

    return null;
  }, [loadOrGenerateKeys, loadLegacyChatPrivateKey]);

  // Reset state and refs when chatId changes (important when navigating between chats)
  useEffect(() => {
    // Clear refs for the new chatId - they'll be reloaded on next initializeEncryption call
    sharedKeyRef.current = null;
    keysRef.current = null;
    setPublicKey(null);
    setState({
      isInitialized: false,
      isEnabled: false,
      hasSharedKey: false,
      error: null,
    });
  }, [chatId]);

  useEffect(() => {
    return () => {
      sharedKeyRef.current = null;
      keysRef.current = null;
    };
  }, []);

  return {
    state,
    publicKey,
    initializeEncryption,
    setPeerPublicKey,
    encryptMessage,
    decryptMessage,
    encryptForPeer,
    decryptFromPeer,
    decryptFromPeerWithFallback,
    encryptFileData,
    encryptFileForPeer,
    decryptFileData,
    decryptFileFromPeer,
    decryptFileFromPeerWithFallback,
    encryptFileWithRawKey,
    decryptFileWithRawKey,
    isAvailable,
  };
};
